<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->


<template>
  <div class="wy-interactions-contact-reasons-detail-topic-group" v-loading="loading">
    <div class="topic-group-section">
      <div class="div-group-viewer-header div-group-name">
        Group includes
      </div>
      <div class="div-group-viewer-content" v-loading="topicLoading">
        <div class="div-group-topics">
          <div class="div-topic-content">
            <div v-for="(topic, index) in groupTopicsList" :label="topic.topicName" :key="index"
              v-bind:class="{ 'div-item': true, selected: topic && topic.selected }"
              @click="getMessagesByTopics(topic, 0, $event)">
              <div :title="topic.topicName" class="div-ellipsis-item">{{ topic.topicName }}</div>
              <div class="div-total" @mouseover="mouseover($event, topic.total, '')" @mouseout="mouseout">({{ formatterNumber(topic.total) }})</div>
              <el-button type="primary" size="mini"
                v-if="isRoleAllow([4601]) && parent.currentItem.isEditable  && (topic.topicName != parent.currentItem.topicDisplayName) && groupTopicsList.length > 1"
                icon="el-icon-error" @click="handleRemoveTopic(topic, $event)"></el-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showMessageContent" class="topic-messages-section">
      <div class="div-topic-messages-viewer-header">Contacts</div>
      <div class="div-topic-messages-viewer-content" v-loading="loadingMsg">
        <div ref="conversationMessages" class="div-topic-message-name" v-for="(item, index) in topicMessages" :key="index"
          v-bind:class="{ selected: (selectedMessage && selectedMessage.conversationId == item.conversationId) }"
          @click="getTranscriptById(item, $event)">
          <div class="message-date">{{ formatMessageHeaderDate(item.date) }}</div>
          <div class="message-right">
            <div class="message-date-time">
              {{ getTimeRange(item) }}
            </div>
          </div>
        </div>
      </div>
      <div class="div-group-viewer-footer div-group-name">
        <div class="div-message-transcripts-viewer-footer">
          <div class="div-transcript-page-infor">Showing {{ pageItemStart }}-{{ pageItemEnd }}</div>
          <div class="div-transcript-pagination">
            <el-pagination :page-size="pageSize" :page-count="pageCount" :current-page="pageNo" :disabled="loadingMsg" prev-text="< Previous"
              next-text="Next >" @current-change="pageChanged" layout="prev, next">
            </el-pagination>
          </div>
        </div>
      </div>
    </div>

    <wy-interactions-transcript-viewer
      :messagesTranscripts="topicMessagesTranscripts" :selectedMessage="selectedMessage" :loading="(loadingMsg || loadingTranscriptMsg)"
      viewName="Grouped Topics" />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions, mapMutations } from 'vuex';
import WyUtils from '../../../library/wy-utils.js';
import WyInteractionsTranscriptViewer from './wy-interactions-transcript-viewer.vue';

export default Vue.extend({
  name: 'wy-interactions-contact-reasons-detail-topic-group',
  props: ['parent', 'activeTab'],
  components: {
    'wy-interactions-transcript-viewer': WyInteractionsTranscriptViewer
  },

  data() {
    return {
      loading: false,
      topicLoading: false,
      loadingMsg: false,
      loadingTranscriptMsg: false,
      initDate: null,
      groupTopics: [],
      topicData: {},
      topicMessages: [],
      selectedMessage: null,
      topicMessagesTranscripts: [],
      updatedTimestamp: (new Date()).getTime(),

      showMessageContent: false,
      showTranscriptContent: false,

      pageNo: 1,
      pageSize: 99, // backend returns 0-99 (total 100)
      pageCount: 1,
      pageItemStart: 0,
      pageItemEnd: 0,
      fetchData: true
    }
  },

  computed: {
    ...mapGetters(['dataModel', 'isRoleAllow', 'processList', 'transcriptInfoReference']),

    isMergedGroup() {
      return (this.parent.currentItem.displayName != this.parent.currentItem.displayNameOrigin);
    },

    groupTopicsList() {
      const foundProcess = this.processList.find(el => (el.action == "unmerge" && el.processStatus != "finished" && el.detail && el.detail.topicDisplayName == this.parent.currentItem.topicDisplayName));
      if (foundProcess) {
        return this.groupTopics.filter(el => (this.updatedTimestamp && !el.deleted && !foundProcess.detail.topicNames.includes(el.topicName)));

      }
      else {
        return this.groupTopics.filter(el => (this.updatedTimestamp && !el.deleted));
      }
    }
  },

  methods: {
    ...mapActions(['fetchContactReasonsTopicConversations', 'fetchTopicsByConfidenceScore', 'fetchContactReasonsTopicConversationDetail', 'unmergeContactReasons']),
    ...mapMutations(['updateTranscriptInfoReference']), //handle transcript info

    handleTranscriptPopover() {
      if (this.transcriptInfoReference) {
        this.transcriptInfoReference.visible = false;
        this.updateTranscriptInfoReference(null);
      }
    },

    pageChanged(pageNo) {
      const selectedTopic = this.groupTopicsList.find(el => el && el.selected);
      if (selectedTopic) {
        this.getMessagesByTopics(selectedTopic, pageNo);
      }
    },

    handleTimeInNiceFormat(Millis) {
      let seconds = Millis / 1000;
      return WyUtils.toNiceTimeFormat(seconds);
    },

    formatterNumber(value) {
      return WyUtils.formatterNumber(value);
    },

    async startLoading() {
      this.initDate = new Date();
      if (!this.loading) {
        this.logEventLoaded();
      }
      await this.loadTopicGroup();
    },

    logEventLoaded() {
      if (this.initDate) {
        this.initDate = null;
      }
    },

    toTranscriptLabel(val) {
      return WyUtils.toTranscriptLabel(val);
    },

    formatMessageDate(date) {
      return WyUtils.formatDate(date);
    },

    formatMessageHeaderDate(date) {
      return WyUtils.formatDateWithDay(date);
    },

    getTimeRange(transcript) {
      if(transcript.msgTime) {
        return transcript.msgTime
      }
      else {
        // TODO: This is for V2 API and can be removed later 
        return WyUtils.getTimeRange(transcript);
      }
    },

    async handleRemoveTopic(topic, event) {
      event.preventDefault();
      event.stopPropagation();

      let unMergeObject = {
        topicDisplayName: this.parent.currentItem.topicDisplayName,
        topicsToUnmerge: [topic.topicName]
      }

      this.topicLoading = true;
      const res = await this.unmergeContactReasons(unMergeObject);
      this.topicLoading = false;

      if (!res) {
        this.$root.showNotificationLocale("error", "error.serverErrorTitle", "error.serverErrorMsg");
      }
      else if (res.status == 200) {
        topic.deleted = true;
        this.updatedTimestamp = (new Date()).getTime();

        if (this.groupTopicsList.length > 0) {
          this.getMessagesByTopics(this.groupTopicsList[0], 0);
        }

        //this.$root.showProcessWaitDialogue({ visible: true, processId: res.data.processId });
        this.parent.parent.refreshTableData();
      }
      else if (res.status == 409) {
        this.$root.showProcessErrorDialogue({ controller: this, info: res.data });
      }
    },

    async getMessagesByTopics(topic, pageNo) {
      this.groupTopicsList.map(el => { el.selected = false });
      if (topic) topic.selected = true;

      if (pageNo) {
        this.pageNo = pageNo;
      }
      else {
        this.pageNo = 1;
        this.pageItemStart = 0;
        this.pageItemEnd = 0;
        this.pageCount = 1;
      }

      this.showMessageContent = true;
      this.topicMessages = [];
      this.showTranscriptContent = false;

      this.loadingMsg = true;

      const result = await this.fetchContactReasonsTopicConversations({ 
        from: 'topicGroup', 
        ...(topic ? { topics: [topic.topicName] } : {topics: []}),
        topicDisplayName: this.topicData.topicDisplayName, 
        pageNo: this.pageNo, 
        pageSize: this.pageSize + 1, 
        isOthers: this.parent.currentItem.isOthers
      })

      if (result && result.data && result.data.length > 0) {
        await this.getTranscriptById(result.data[0]);

        if (result.data.length > this.pageSize) {
          this.topicMessages = result.data
          this.pageItemStart = (this.pageNo - 1) * (this.pageSize + 1) + 1;
          this.pageItemEnd = this.pageItemStart + this.pageSize;
          this.pageCount = this.pageNo + 1;
        }
        else {
          this.topicMessages = result.data;
          this.pageItemStart = (this.pageNo - 1) * (this.pageSize + 1) + 1;

          this.pageItemEnd = this.pageItemStart + result.data.length - 1;
        }
        //this.selectedMessage = this.topicMessages[0];
        //this.topicMessagesTranscripts = this.selectedMessage.messages;
        this.showTranscriptContent = true;
      }
      else {
        this.topicMessages = [];
        this.topicMessagesTranscripts = [];
        this.showTranscriptContent = false;
        this.pageNo = 1;
        this.pageItemStart = 0;
        this.pageItemEnd = 0;
      }
      this.loadingMsg = false;
    },

    async getTranscriptById(item) {
      this.topicMessagesTranscripts = [];
      this.showTranscriptContent = false;
      this.loadingTranscriptMsg = true;

      let resultDetail = await this.fetchContactReasonsTopicConversationDetail({ conversationId: item.conversationId, isOthers: this.parent.currentItem.isOthers  })
      if(resultDetail && resultDetail.data) {
        this.selectedMessage = resultDetail.data;
        this.topicMessagesTranscripts = this.selectedMessage.messages;
      }
      this.loadingTranscriptMsg = false;
    },

    async loadTopicGroup() {
      if (this.fetchData && this.parent.currentItem) {
        this.loadingMsg = true;
        this.fetchData = false;
        this.showMessageContent = false;
        this.showTranscriptContent = false;
        this.topicData = this.parent.currentItem;

        if ((this.parent.currentItem.mergedTopics && (this.parent.currentItem.mergedTopics.length > 1 && !this.parent.currentItem.isOthers)) || this.parent.currentItem.isOthers) {
          const result = await this.fetchTopicsByConfidenceScore({ 
              from: 'topicGroup', 
              topicDisplayName: this.topicData.topicDisplayName, 
              pageNo: this.pageNo, 
              pageSize: this.pageSize + 1, 
              isOthers: this.parent.currentItem.isOthers,
              topics: this.topicData.mergedTopics,
            })

            if (result.data && result.data.length > 0) {
              this.parent.currentItem.groupTopics = result.data.map(item => { return { topicName: item.topic, total: item.totalVolume } });
            }
        }else{
          this.parent.currentItem.groupTopics = this.parent.currentItem.mergedTopics.map(topic => { return { topicName: topic, total: this.parent.currentItem.totalVolume } });
        } 
        this.groupTopics = this.parent.currentItem.groupTopics;
        this.getMessagesByTopics(this.groupTopics[0], 0);
      }
      this.loadingMsg = false;
      this.parent.stopLoadingByTab('groupedTopics');
    },

    updateCurrentRow() {
      this.parent.tableModel.results.forEach((row) => {
        if (row.topicDisplayName == this.parent.currentItem.topicDisplayName) {
          this.parent.currentItem = row;
        }
      })
    },
    mouseover(e, tooltips) {
      e.stopPropagation();
      let tooltipsEl = document.querySelector(".site-tooltips");
      if(tooltipsEl) {
        tooltipsEl.classList.add('black-background');
        tooltipsEl.classList.add('bottom-arrow');
        tooltipsEl.innerHTML = this.formatterNumber(tooltips);
        tooltipsEl.style.display = 'block';
        tooltipsEl.style.left = `${e.target.getBoundingClientRect().x + 10}px`;
        tooltipsEl.style.top = `${e.target.getBoundingClientRect().top + e.target.getBoundingClientRect().height - 55}px`;
        tooltipsEl.style.left = `${e.target.getBoundingClientRect().x - tooltipsEl.getBoundingClientRect().width / 2 + e.target.getBoundingClientRect().width / 2 + 25}px`;
      }
    },
    mouseout(e) {
      e.stopPropagation();
      let tooltipsEl = document.querySelector(".site-tooltips");
      if(tooltipsEl) {
        tooltipsEl.style.display = 'none';
        tooltipsEl.classList.remove('bottom-arrow');
      }
    }
  },

  // created() {
  //   this.loadTopicGroup();
  // },
  mounted() {
    // on resize window, we close the transcript popover
    window.addEventListener('resize', this.handleTranscriptPopover);
    // Outside of metric-analysis
    document.addEventListener('click', () => {
      this.handleTranscriptPopover();
    });
    // Inside metric-analysis
    document.querySelector('.wy-interactions-contact-reasons-detail-topic-group').addEventListener('click', () => {
      this.handleTranscriptPopover();
    });
  }
});
</script>

<style lang="scss">
.wy-interactions-contact-reasons-detail-topic-group {
  display: inline-flex;
  width: 100%;
  height: calc(100vh - 287px);
  // min-width: 1093px;
  min-width: 1176px;

  .transcripts-viewer-header-right {
    overflow-y: auto;
    height: 204px !important;
    padding-right: 20px;

    &::-webkit-scrollbar {
      background-color: #000 !important;
      width: 8px !important;
    }

    &::-webkit-scrollbar-thumb {
      background: #C8C8C8 !important;
      border-radius: 5px;
    }
  }

  .contact-reason-transcript-tab-info-pop-over {
    padding: 12px 7px 10px 15px !important;
  }

  .sentiment-icon-positive {
    cursor: pointer;
    background-image: url('../../../assets/icons/icon_positive_senti.svg');
    background-size: 80%;
    background-repeat: no-repeat;
    background-position-y: 3px;
    background-position-x: 5px;
    position: relative;
    right: 67px;
    bottom: 8.5px;
    border-radius: 100px;
    border: 0px;
    margin: 6px 13px 0 0;
    padding: 12px 10px 14px 14px;
    float: right;
  }

  .sentiment-icon-neutral {
    cursor: pointer;
    background-image: url('../../../assets/icons/icon_neutral_senti.svg');
    background-size: 80%;
    background-repeat: no-repeat;
    background-position-y: 3px;
    background-position-x: 5px;
    position: relative;
    right: 67px;
    bottom: 8.5px;
    border-radius: 100px;
    border: 0px;
    margin: 6px 13px 0 0;
    padding: 12px 10px 14px 14px;
    float: right;
  }

  .sentiment-icon-negative {
    cursor: pointer;
    background-image: url('../../../assets/icons/icon_negative_senti.svg');
    background-size: 80%;
    background-repeat: no-repeat;
    background-position-y: 3px;
    background-position-x: 5px;
    position: relative;
    right: 67px;
    bottom: 8.5px;
    border-radius: 100px;
    border: 0px;
    margin: 6px 13px 0 0;
    padding: 12px 10px 14px 14px;
    float: right;
  }


  .contact-reason-group-topics-tab-info-pop-over {
    background: white;
    border: black !important;
    // color: #000000;
    font-size: 14px;
    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  }

  .topic-group-section {
    min-width: 540px;
    max-width: 540px;
    margin-right: 15px;
    margin-bottom: 20px;
    width: 25%;
    padding: 10px 0px 10px 0px;
    background: #FFFFFF 0% 0% no-repeat padding-box;
    border: 1px solid #E8E8E8;
    border-radius: 10px;
    opacity: 1;

    .div-group-viewer-content {
      margin-top: 10px;
      border-top: 1px solid #E8E8E8;
      height: calc(100vh - 355px);
      overflow-y: auto;
      overflow-x: hidden;
      margin-bottom: 12px;

      &::-webkit-scrollbar {
        width: 20px;
      }
      &::-webkit-scrollbar-thumb {
        border-radius: 160px;
        border: 2px solid transparent;
        border-top: 4px solid transparent;
        background-clip: content-box;
        background-color: #CAD2DA;
      }
      &::-webkit-scrollbar-track {
        background:#F3F6F8;
        border-top: 1px solid #CAD2DA;
        border-bottom-right-radius: 8px !important;

      }
      &::-webkit-scrollbar:vertical {
        width: 14px;
      }
      &::-webkit-scrollbar-thumb:vertical {
        border-radius: 160px;
        border: 2px solid transparent;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        background-clip: content-box;
        background-color: #CAD2DA;
      }
      &::-webkit-scrollbar-track:vertical {
        // border-left: 1px solid #CAD2DA;
        border-top: none !important;
      }

    }

    .div-group-viewer-header.div-group-name {
      font-size: 16px;
      font-weight: 500;
      padding: 0px 10px 5px 30px;
    }

    .div-group-topics {
      margin-top: 0px;

      .div-topic-content {
        height: fit-content;

        .div-item {
          color: var(--site-font-primary-color);
          font-weight: normal;
          margin: 0px 0px 0px 0px;
          line-height: 19px;
          padding: 9px 20px 3px 30px;
          font-size: 12px;
          // width: 88%;
          white-space: nowrap;
          overflow: hidden;
          // text-overflow: ellipsis;
          cursor: pointer;

          .div-ellipsis-item {
            width: calc(100% - 100px);
            display: inline-block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            margin-right: 20px;
          }
          .div-total {
            display: inline-block;
            width: 50px;
            text-align: right;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            margin-right: 10px;
          }

          button {
            width: 15px;
            height: 15px;
            border: 0;
            padding: 0;
            margin: 0;
            background-color: #fff;

            .el-icon-error {
              top: -3px;
            }
          }
        }

        .div-item:hover {
          background: #f5f7fa;
        }

        .div-item.selected {
          background: #E2ECF7;

          button {
            background-color: #E2ECF7;
          }
        }

        .div-item.disabled {
          button {
            opacity: 0.3 !important;
            cursor: default;

            .el-icon-error {
              cursor: default;
            }
          }
        }

        .el-icon-error {
          float: right;
          // display: none;
          top: 2px;
          position: relative;
          color: #80A6CD;
          cursor: pointer;
          font-size: 15px;
        }

        .el-icon-error.is-disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .div-item:hover .el-icon-error {
          display: block;
        }
      }
    }
  }

  .topic-messages-section {
    min-width: 300px;
    max-width: 300px;
    margin-bottom: 20px;
    width: 300px;
    padding: 7px 0px 10px 0px;
    background: #FFFFFF 0% 0% no-repeat padding-box;
    border: 1px solid #E8E8E8;
    border-radius: 10px 0px 0px 10px;
    opacity: 1;

    .el-loading-mask {
      height: unset !important;
      opacity: unset;
      background-color: unset;

      .el-loading-spinner {
        top: 35%;
        background: var(--site-loading-icon);
        background-repeat: no-repeat;
        background-size: 25px;
        background-position: center;
      }

      .el-loading-spinner .circular {
        display: unset !important;
      }
    }

    .div-topic-messages-viewer-header {
      font-size: 16px;
      font-weight: 500;
      padding: 4px 10px 9px 30px;
    }

    .div-topic-messages-viewer-content {
      margin-top: 5px;
      border-top: 1px solid #E8E8E8;
      border-bottom: 1px solid #E8E8E8;
      height: calc(100vh - 384px);
      overflow-y: auto;
      overflow-x: hidden;
      margin-bottom: 5px;
      //padding-top: 10px;

      .div-topic-message-name {
        font-size: 12px;
        word-break: keep-all;
        cursor: pointer;
        color: var(--site-font-primary-color);
        font-weight: normal;
        margin: 0px 0px 0px 0px;
        line-height: 12px;
        padding: 12px 10px 11px 30px;

        .message-date {
          width: 105px;
          display: inline-block;
        }

        .message-right {
          float: right;
          width: 130px;

          .message-date-time {
            text-align: left;
            padding-right: 10px;
          }
        }
      }

      .div-topic-message-name:hover {
        background: #f5f7fa;
      }

      .div-topic-message-name.selected {
        background: #E2ECF7;
      }
      
      &::-webkit-scrollbar {
        width: 20px;
      }
      &::-webkit-scrollbar-thumb {
        border-radius: 160px;
        border: 2px solid transparent;
        border-top: 4px solid transparent;
        background-clip: content-box;
        background-color: #CAD2DA;
      }
      &::-webkit-scrollbar-track {
        background:#F3F6F8;
        border-top: 1px solid #CAD2DA;
      }
      &::-webkit-scrollbar:vertical {
        width: 14px;
      }
      &::-webkit-scrollbar-thumb:vertical {
        border-radius: 160px;
        border: 2px solid transparent;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        background-clip: content-box;
        background-color: #CAD2DA;
      }
      &::-webkit-scrollbar-track:vertical {
        // border-left: 1px solid #CAD2DA;
        border-top: none !important;
      }

    }
  }

  .message-transcripts-section {
    padding: 7px 0px 0px 0px;
    margin-bottom: 20px;
  }

  .div-group-viewer-footer.div-group-name {
    font-size: 16px;
    font-weight: 500;
    padding: 0px 0px 0px 0px;
  }

  .div-message-transcripts-viewer-footer {
    display: inline-block;
    width: 100%;

    .div-transcript-page-infor {
      float: left;
      padding-top: 2px;
      padding-left: 14px;
      font-size: 12px;
      color: #ADADAD;
    }

    .div-transcript-pagination {
      float: right;
      padding-right: 14px;
      font-size: 12px;
      color: #ADADAD;

      .el-pagination {
        padding-right: 0px;
      }

      .el-pagination button {
        color: #002852;
        font-weight: bold;
        padding-right: 0px;
        padding-left: 30px;
        font-size: 12px;
        line-height: unset !important;
        height: unset !important;

        >span {
          line-height: unset !important;
          height: unset !important;
        }
      }

      .el-pagination button.btn-next {
        padding-left: 10px;
      }

      .el-pagination button:disabled {
        color: #C0C4CC;
      }

      .el-pagination span:not([class*=suffix]) {
        word-spacing: 7px;
      }
    }
  }
}

.contact-reason-group-topics-tab-info-pop-over {
  font-size: 13px;
  width: fit-content !important;
  color: #E4E8EC;
  padding: 12px 7px 10px 15px;
  box-shadow: 0px 0px 20px #0000002E;
  border-radius: 10px;
  line-height: 18px;

  .popper__arrow {
    display: block !important;
  }

  .popper-item {
    margin-bottom: 15px;
  }
}

.site-tooltips.bottom-arrow:after {
  transform: rotateZ(180deg);
  bottom: 0%;
  top: 95%;
  left: 20% !important;
}
</style>